import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  IconButton,
  Switch,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  MapContainer,
  TileLayer,
  Marker,
  Polyline,
  Popup,
  useMap
} from 'react-leaflet';
import L from 'leaflet';
import dayjs from 'dayjs';
import axios from 'axios';
import AppHeader from '../../components/AppHeader';

import MapIcon from '@mui/icons-material/Map';
import RefreshIcon from '@mui/icons-material/Refresh';
import PersonPinIcon from '@mui/icons-material/PersonPin';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import SettingsIcon from '@mui/icons-material/Settings';
import SearchIcon from '@mui/icons-material/Search';

// Fix Leaflet marker icon issue in React
const blueIcon = new L.Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const startIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const endIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

// Helper component to auto-recenter map when coordinates change
const AutoRecenterMap = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (points && points.length > 0) {
      const bounds = L.latLngBounds(points.map(p => [p.latitude, p.longitude]));
      map.fitBounds(bounds, { padding: [40, 40] });
    }
  }, [points, map]);
  return null;
};

const API_BASE = process.env.REACT_APP_API_URL1 || 'http://localhost:3000/api';

const getBearerToken = () =>
  localStorage.getItem('userToken') ||
  localStorage.getItem('token') ||
  sessionStorage.getItem('userToken') ||
  sessionStorage.getItem('token') ||
  '';

const getHeaders = () => ({
  headers: {
    Authorization: `Bearer ${getBearerToken()}`,
    'X-Tenant': (localStorage.getItem('tenant') || '').trim()
  }
});

const RouteMapManager = ({ setIsLoggedIn }) => {
  const [staffList, setStaffList] = useState([]);
  const [selectedStaffId, setSelectedStaffId] = useState('');
  const [selectedDate, setSelectedDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [routePoints, setRoutePoints] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [loadingRoute, setLoadingRoute] = useState(false);
  const [error, setError] = useState(null);
  const [previewPhoto, setPreviewPhoto] = useState(null);

  // Permission settings modal state
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [allStaffList, setAllStaffList] = useState([]);
  const [loadingAllStaff, setLoadingAllStaff] = useState(false);
  const [permissionToggling, setPermissionToggling] = useState({});
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchStaffWithCheckins();
  }, [selectedDate]);

  useEffect(() => {
    if (selectedStaffId) {
      fetchStaffRoute(selectedStaffId, selectedDate);
    } else {
      setRoutePoints([]);
    }
  }, [selectedStaffId, selectedDate]);

  useEffect(() => {
    if (settingsOpen) {
      fetchAllStaff();
    }
  }, [settingsOpen]);

  const fetchStaffWithCheckins = async () => {
    setLoadingStaff(true);
    setError(null);
    try {
      const res = await axios.get(`${API_BASE}/checkpoints/staff?date=${selectedDate}`, getHeaders());

      if (res.data?.success) {
        const list = res.data.data || [];
        setStaffList(list);
        if (list.length > 0 && !selectedStaffId) {
          setSelectedStaffId(list[0].id);
        }
      }
    } catch (err) {
      console.error('Failed to fetch field staff:', err);
      setError('Failed to load field staff list.');
    } finally {
      setLoadingStaff(false);
    }
  };

  const fetchAllStaff = async () => {
    setLoadingAllStaff(true);
    try {
      const res = await axios.get(`${API_BASE}/checkpoints/staff?all=true`, getHeaders());
      if (res.data?.success) {
        setAllStaffList(res.data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch all staff:', err);
    } finally {
      setLoadingAllStaff(false);
    }
  };

  const fetchStaffRoute = async (staffId, date) => {
    setLoadingRoute(true);
    setError(null);
    try {
      const res = await axios.get(`${API_BASE}/checkpoints/route/${staffId}?date=${date}`, getHeaders());

      if (res.data?.success) {
        setRoutePoints(res.data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch staff route:', err);
      setError('Failed to load route data for selected staff.');
    } finally {
      setLoadingRoute(false);
    }
  };

  const handleTogglePermission = async (staffId, currentStatus) => {
    setPermissionToggling(prev => ({ ...prev, [staffId]: true }));
    try {
      const newStatus = !currentStatus;
      await axios.put(
        `${API_BASE}/checkpoints/permission/${staffId}`,
        { allow_field_checkin: newStatus },
        getHeaders()
      );

      setAllStaffList(prev =>
        prev.map(s => (s.id === staffId ? { ...s, allow_field_checkin: newStatus ? 1 : 0 } : s))
      );
      setStaffList(prev =>
        prev.map(s => (s.id === staffId ? { ...s, allow_field_checkin: newStatus ? 1 : 0 } : s))
      );
      // Refresh checkin staff list if permission granted
      fetchStaffWithCheckins();
    } catch (err) {
      console.error('Failed to update permission:', err);
      alert('Failed to update staff check-in permission.');
    } finally {
      setPermissionToggling(prev => ({ ...prev, [staffId]: false }));
    }
  };

  const polylineCoords = routePoints.map(pt => [parseFloat(pt.latitude), parseFloat(pt.longitude)]);
  const centerPosition = routePoints.length > 0
    ? [parseFloat(routePoints[0].latitude), parseFloat(routePoints[0].longitude)]
    : [16.8661, 96.1951]; // Default Yangon coords

  const selectedStaffObj = staffList.find(s => String(s.id) === String(selectedStaffId));

  return (
    <Box sx={{ flexGrow: 1, bgcolor: '#f4f6f8', minHeight: '100vh', pb: 4, width: '100%', maxWidth: '100vw', boxSizing: 'border-box', overflowX: 'hidden' }}>
      <AppHeader setIsLoggedIn={setIsLoggedIn} />

      <Box sx={{ px: 3, mt: 3, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        {/* Header Title & Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <MapIcon color="primary" sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h5" fontWeight="bold" color="text.primary">
                Field Check-in Route Map
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Track real-time checkpoint visits and travel paths for field, sales, and security staff.
              </Typography>
            </Box>
          </Box>

          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => setSettingsOpen(true)}
          >
            Manage Permissions
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Filter Toolbar */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                size="small"
                value={selectedDate}
                onChange={e => setSelectedDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="staff-select-label">Field Staff Member</InputLabel>
                <Select
                  labelId="staff-select-label"
                  value={selectedStaffId}
                  label="Field Staff Member"
                  onChange={e => setSelectedStaffId(e.target.value)}
                  disabled={loadingStaff}
                >
                  {staffList.map(staff => (
                    <MenuItem key={staff.id} value={staff.id}>
                      {staff.name} ({staff.staff_code || `ID: ${staff.id}`}) — {staff.checkin_count || 0} visits
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={2} md={2}>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={() => fetchStaffRoute(selectedStaffId, selectedDate)}
                disabled={!selectedStaffId || loadingRoute}
              >
                Refresh
              </Button>
            </Grid>

            {selectedStaffObj && (
              <Grid item xs={12} md={3} sx={{ textAlign: 'right' }}>
                <Chip
                  icon={<PersonPinIcon />}
                  label={`${selectedStaffObj.name} (${routePoints.length} Check-ins)`}
                  color="primary"
                  variant="outlined"
                />
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Map & Timeline Flex Layout (75% Map / 25% Visit Log) */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
          {/* Map Column (75% of total width) */}
          <Box sx={{ flex: 3, minWidth: 0 }}>
            <Paper sx={{ p: 2, borderRadius: 2, height: 620, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, alignItems: 'center' }}>
                <Typography variant="h6" fontWeight="bold">
                  🗺️ Route Visualization (Blue Line)
                </Typography>
                {loadingRoute && <CircularProgress size={20} />}
              </Box>

              <Box sx={{ flexGrow: 1, borderRadius: 1, overflow: 'hidden', border: '1px solid #e0e0e0', position: 'relative' }}>
                <MapContainer
                  center={centerPosition}
                  zoom={14}
                  style={{ width: '100%', height: '100%' }}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />

                  <AutoRecenterMap points={routePoints} />

                  {/* Draw Blue Line Route */}
                  {polylineCoords.length > 1 && (
                    <Polyline
                      positions={polylineCoords}
                      color="#1976d2"
                      weight={4}
                      opacity={0.8}
                      dashArray="8, 8"
                    />
                  )}

                  {/* Render Markers for Checkins */}
                  {routePoints.map((pt, idx) => {
                    const isFirst = idx === 0;
                    const isLast = idx === routePoints.length - 1 && routePoints.length > 1;
                    let icon = blueIcon;
                    if (isFirst) icon = startIcon;
                    else if (isLast) icon = endIcon;

                    return (
                      <Marker
                        key={pt.id}
                        position={[parseFloat(pt.latitude), parseFloat(pt.longitude)]}
                        icon={icon}
                      >
                        <Popup>
                          <Box sx={{ minWidth: 180, p: 0.5 }}>
                            <Typography variant="subtitle2" fontWeight="bold" color="primary">
                              #{idx + 1} - Check-in Visit
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary">
                              🕒 {dayjs(pt.checkin_time).format('hh:mm:ss A')}
                            </Typography>
                            <Typography variant="caption" display="block">
                              📍 {parseFloat(pt.latitude).toFixed(5)}, {parseFloat(pt.longitude).toFixed(5)}
                            </Typography>
                            {pt.remark && (
                              <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', bgcolor: '#f5f5f5', p: 0.5, borderRadius: 1 }}>
                                "{pt.remark}"
                              </Typography>
                            )}
                            {pt.photo_url && (
                              <Button
                                size="small"
                                startIcon={<PhotoCameraIcon />}
                                sx={{ mt: 1 }}
                                onClick={() => setPreviewPhoto(pt.photo_url)}
                              >
                                View Photo
                              </Button>
                            )}
                          </Box>
                        </Popup>
                      </Marker>
                    );
                  })}
                </MapContainer>
              </Box>
            </Paper>
          </Box>

          {/* Timeline & Details Column (25% of total width) */}
          <Box sx={{ flex: 1, minWidth: 280 }}>
            <Paper sx={{ p: 2, borderRadius: 2, height: 620, overflowY: 'auto' }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                📋 Visit History Log ({routePoints.length})
              </Typography>

              {loadingRoute ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <CircularProgress />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Loading route logs...
                  </Typography>
                </Box>
              ) : routePoints.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <LocationOnIcon sx={{ fontSize: 48, color: '#ccc' }} />
                  <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                    No check-ins recorded for this staff on {selectedDate}.
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#fafafa' }}>
                        <TableCell>#</TableCell>
                        <TableCell>Time</TableCell>
                        <TableCell>Remark / Location</TableCell>
                        <TableCell align="right">Photo</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {routePoints.map((pt, idx) => (
                        <TableRow key={pt.id} hover>
                          <TableCell sx={{ fontWeight: 'bold' }}>{idx + 1}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {dayjs(pt.checkin_time).format('hh:mm A')}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {pt.remark || 'Standard Checkpoint'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {parseFloat(pt.latitude).toFixed(4)}, {parseFloat(pt.longitude).toFixed(4)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {pt.photo_url ? (
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => setPreviewPhoto(pt.photo_url)}
                              >
                                <PhotoCameraIcon fontSize="small" />
                              </IconButton>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Box>
        </Box>
      </Box>

      {/* Photo Preview Dialog */}
      <Dialog open={Boolean(previewPhoto)} onClose={() => setPreviewPhoto(null)} maxWidth="md">
        <DialogTitle>Verification Photo</DialogTitle>
        <DialogContent>
          {previewPhoto && (
            <Box
              component="img"
              src={(() => {
                let url = previewPhoto;
                const token = getBearerToken();
                const tenant = (localStorage.getItem('tenant') || sessionStorage.getItem('tenant') || '').trim();
                if (!url.includes('/')) {
                  url = `${API_BASE}/admin/documents/${url}/preview`;
                }
                if (!url.includes('token=')) {
                  const hasParams = url.includes('?');
                  url += `${hasParams ? '&' : '?'}token=${token}${(!url.includes('tenant=') && tenant) ? `&tenant=${tenant}` : ''}`;
                }
                return url;
              })()}
              alt="Checkin verification"
              sx={{ width: '100%', maxHeight: 500, objectFit: 'contain', borderRadius: 1 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewPhoto(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Manage Permissions Dialog */}
      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Field Check-in Staff Permissions</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enable or disable the "Field Check-in" quick action button on the mobile app for staff members.
          </Typography>

          <TextField
            fullWidth
            size="small"
            placeholder="Search staff by name or staff code..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          {loadingAllStaff ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Loading staff permissions...
              </Typography>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 380 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Staff Code</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell align="center">Field Check-in</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allStaffList
                    .filter(s =>
                      (s.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                      (s.staff_code || '').toLowerCase().includes(searchQuery.toLowerCase())
                    )
                    .map(s => (
                      <TableRow key={s.id} hover>
                        <TableCell>{s.staff_code || s.id}</TableCell>
                        <TableCell>{s.name}</TableCell>
                        <TableCell align="center">
                          <Switch
                            checked={Boolean(s.allow_field_checkin)}
                            onChange={() => handleTogglePermission(s.id, Boolean(s.allow_field_checkin))}
                            disabled={permissionToggling[s.id]}
                            color="primary"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RouteMapManager;
